import { Component, computed, effect, inject, Signal, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { CampaignsService } from '../services/campaigns.service';
import { AnalyticsAttributesV4, Campaign } from '@brightside-web/desktop/data-access/shared';
import { CampaignCTA, CampaignSection } from '../models/campaign.models';
import { BrightsideUiModule } from '@brightside/brightside-ui';
import { MicroUiMoleculeModule } from '@micro-ui/molecule';
import { TranslateModule } from '@ngx-translate/core';
import { SharedDesktopModule } from '@brightside-web/shared/desktop';
import { Environment } from '@micro-core/environment';
import { getRouteForKey } from '@brightside-web/desktop/data-access/core-services';

@Component({
    selector: 'bw-campaigns',
    imports: [CommonModule, BrightsideUiModule, MicroUiMoleculeModule, TranslateModule, SharedDesktopModule],
    templateUrl: './campaigns.component.html',
    styleUrl: './campaigns.component.scss'
})
export class CampaignsComponent {
  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _campaignsService = inject(CampaignsService);
  private _activeSection = signal<CampaignSection | undefined>(undefined);
  private _env = inject(Environment);

  public readonly activeSection = this._activeSection.asReadonly();

  routeParam = toSignal(this._route.paramMap, { initialValue: null });

  campaignHeaderImg = computed(() => {
    const section = this.activeSection();
    return section?.image ? `${this._env.cdnUrl}images/app/${section?.image}.png` : null;
  });

  eventObject: Signal<AnalyticsAttributesV4 | undefined> = computed(() => {
    const activeCampaign = this._campaignsService.activeCampaign();
    const params = this.routeParam();
    if (!params || !activeCampaign) return;

    const currentRoute = params.get('route');

    if (currentRoute === 'intro') {
      return { event_name: 'campaign_intro', custom: { id: activeCampaign.key } };
    } else {
      return { event_name: 'campaign', custom: { id: activeCampaign.key } };
    }
  });

  constructor() {
    effect(
      () => {
        const params = this.routeParam();
        const activeCampaign = this._campaignsService.activeCampaign();
        if (!params || !activeCampaign) return;

        const currentRoute = params.get('route');
        if (!currentRoute) return;

        if (currentRoute in activeCampaign) {
          const section = (activeCampaign as unknown as Record<string, CampaignSection>)[currentRoute];
          this._activeSection.set(section);
        }
      },
      { allowSignalWrites: true }
    );
  }

  protected handleCtaClick(cta: CampaignCTA) {
    this._campaignsService.campaignRouteHandler(cta);
  }
}
